import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography, Popper, ClickAwayListener, Portal } from '@mui/material';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import moment from 'moment';
import { useTheme as useThemeContext } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';

// Inject dynamic styles for react-date-range
const injectDateRangeStyles = (isDark) => {
  const styleId = 'react-date-range-custom-styles';
  let styleElement = document.getElementById(styleId);
  
  if (!styleElement) {
    styleElement = document.createElement('style');
    styleElement.id = styleId;
    document.head.appendChild(styleElement);
  }

  const bgColor = isDark ? 'var(--cardBg)' : 'var(--appBg)';
  const textColor = 'var(--secColor4)';
  const borderColor = 'var(--borderColor)';
  const primaryColor = 'var(--primaryColor)';
  const primaryColorTranslucent = 'var(--primaryColorTranslucent)';
  const hoverBg = 'var(--hoverBg)';
  const textSecondary = 'var(--textSecondary)';

  styleElement.textContent = `
    .rdrDateRangeWrapper {
      background-color: ${getComputedStyle(document.documentElement).getPropertyValue('--cardBg') || (isDark ? '#2a2a2a' : '#f8f9fa')} !important;
      color: ${getComputedStyle(document.documentElement).getPropertyValue('--secColor4') || (isDark ? '#FFFFFF' : '#333333')} !important;
      font-family: 'Montserrat', sans-serif !important;
    }
    .rdrCalendarWrapper {
      background-color: ${getComputedStyle(document.documentElement).getPropertyValue('--cardBg') || (isDark ? '#2a2a2a' : '#f8f9fa')} !important;
      color: ${getComputedStyle(document.documentElement).getPropertyValue('--secColor4') || (isDark ? '#FFFFFF' : '#333333')} !important;
      font-family: 'Montserrat', sans-serif !important;
    }
    .rdrDateDisplay {
      background-color: ${getComputedStyle(document.documentElement).getPropertyValue('--appBg') || (isDark ? '#1c1c1c' : '#ffffff')} !important;
      color: ${getComputedStyle(document.documentElement).getPropertyValue('--secColor4') || (isDark ? '#FFFFFF' : '#333333')} !important;
      font-family: 'Montserrat', sans-serif !important;
    }
    .rdrDateDisplayItem {
      background-color: ${getComputedStyle(document.documentElement).getPropertyValue('--cardBg') || (isDark ? '#2a2a2a' : '#f8f9fa')} !important;
      color: ${getComputedStyle(document.documentElement).getPropertyValue('--secColor4') || (isDark ? '#FFFFFF' : '#333333')} !important;
      border: 1px solid ${getComputedStyle(document.documentElement).getPropertyValue('--borderColor') || (isDark ? '#404040' : '#e0e0e0')} !important;
      font-family: 'Montserrat', sans-serif !important;
    }
    .rdrDateDisplayItem input {
      color: ${getComputedStyle(document.documentElement).getPropertyValue('--secColor4') || (isDark ? '#FFFFFF' : '#333333')} !important;
      font-family: 'Montserrat', sans-serif !important;
    }
    .rdrMonthAndYearWrapper {
      background-color: ${getComputedStyle(document.documentElement).getPropertyValue('--appBg') || (isDark ? '#1c1c1c' : '#ffffff')} !important;
      color: ${getComputedStyle(document.documentElement).getPropertyValue('--secColor4') || (isDark ? '#FFFFFF' : '#333333')} !important;
      font-family: 'Montserrat', sans-serif !important;
    }
    .rdrMonthAndYearPickers select {
      color: ${getComputedStyle(document.documentElement).getPropertyValue('--secColor4') || (isDark ? '#FFFFFF' : '#333333')} !important;
      background-color: ${getComputedStyle(document.documentElement).getPropertyValue('--cardBg') || (isDark ? '#2a2a2a' : '#f8f9fa')} !important;
      font-family: 'Montserrat', sans-serif !important;
    }
    .rdrWeekDay {
      color: ${getComputedStyle(document.documentElement).getPropertyValue('--secColor4') || (isDark ? '#FFFFFF' : '#333333')} !important;
      font-family: 'Montserrat', sans-serif !important;
    }
    .rdrDay {
      color: ${getComputedStyle(document.documentElement).getPropertyValue('--secColor4') || (isDark ? '#FFFFFF' : '#333333')} !important;
      font-family: 'Montserrat', sans-serif !important;
    }
    .rdrDayNumber span {
      color: ${getComputedStyle(document.documentElement).getPropertyValue('--secColor4') || (isDark ? '#FFFFFF' : '#333333')} !important;
      font-family: 'Montserrat', sans-serif !important;
    }
    .rdrDayPassive .rdrDayNumber span {
      color: ${getComputedStyle(document.documentElement).getPropertyValue('--textSecondary') || (isDark ? '#b0b0b0' : '#666666')} !important;
      font-family: 'Montserrat', sans-serif !important;
    }
    .rdrDayToday .rdrDayNumber span:after {
      background-color: ${getComputedStyle(document.documentElement).getPropertyValue('--primaryColor') || '#F7463C'} !important;
    }
    .rdrDayActive .rdrDayNumber span,
    .rdrDaySelected .rdrDayNumber span {
      background-color: ${getComputedStyle(document.documentElement).getPropertyValue('--primaryColor') || '#F7463C'} !important;
      color: ${getComputedStyle(document.documentElement).getPropertyValue('--secColor4') || (isDark ? '#FFFFFF' : '#333333')} !important;
      font-family: 'Montserrat', sans-serif !important;
    }
    .rdrDayInRange {
      background-color: ${getComputedStyle(document.documentElement).getPropertyValue('--primaryColorTranslucent') || 'rgba(247, 70, 60, 0.44)'} !important;
      color: ${getComputedStyle(document.documentElement).getPropertyValue('--secColor4') || (isDark ? '#FFFFFF' : '#333333')} !important;
      font-family: 'Montserrat', sans-serif !important;
    }
    .rdrDayHovered {
      background-color: ${getComputedStyle(document.documentElement).getPropertyValue('--hoverBg') || (isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)')} !important;
      font-family: 'Montserrat', sans-serif !important;
    }
    .rdrDayDisabled {
      color: ${getComputedStyle(document.documentElement).getPropertyValue('--textSecondary') || (isDark ? '#b0b0b0' : '#666666')} !important;
      font-family: 'Montserrat', sans-serif !important;
    }
    .rdrNextPrevButton,
    .rdrPprevButton,
    .rdrNextButton {
      background-color: ${getComputedStyle(document.documentElement).getPropertyValue('--cardBg') || (isDark ? '#2a2a2a' : '#f8f9fa')} !important;
      color: ${getComputedStyle(document.documentElement).getPropertyValue('--secColor4') || (isDark ? '#FFFFFF' : '#333333')} !important;
      border: 1px solid ${getComputedStyle(document.documentElement).getPropertyValue('--borderColor') || (isDark ? '#404040' : '#e0e0e0')} !important;
      font-family: 'Montserrat', sans-serif !important;
      cursor: pointer !important;
      pointer-events: auto !important;
      z-index: 100 !important;
      position: relative !important;
    }
    .rdrNextPrevButton button,
    .rdrPprevButton button,
    .rdrNextButton button {
      pointer-events: auto !important;
      cursor: pointer !important;
      z-index: 101 !important;
    }
    .rdrNextPrevButton:hover,
    .rdrPprevButton:hover,
    .rdrNextButton:hover {
      background-color: ${getComputedStyle(document.documentElement).getPropertyValue('--hoverBg') || (isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)')} !important;
    }
    .rdrCalendarWrapper {
      user-select: none;
      pointer-events: auto !important;
    }
    .rdrDay {
      cursor: pointer !important;
    }
    .rdrMonthAndYearWrapper {
      pointer-events: auto !important;
      z-index: 5 !important;
      position: relative !important;
    }
  `;
};

const DateRangePicker = ({ startDate, endDate, onChange, fullWidth = true, margin = 'normal' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef(null);
  const { currentTheme } = useThemeContext();
  const isDark = currentTheme === 'dark';
  const [focusedRange, setFocusedRange] = useState([0, 0]);
  const isUserInteractingRef = useRef(false);
  const { t } = useTranslation();

  // Inject styles when theme changes
  useEffect(() => {
    injectDateRangeStyles(isDark);
  }, [isDark]);

  // Convert moment dates to Date objects for react-date-range
  const rangeStart = startDate ? (startDate instanceof moment ? startDate.toDate() : new Date(startDate)) : null;
  const rangeEnd = endDate ? (endDate instanceof moment ? endDate.toDate() : new Date(endDate)) : null;

  const [selectionRange, setSelectionRange] = useState({
    startDate: rangeStart || new Date(),
    endDate: rangeEnd || rangeStart || new Date(),
    key: 'selection',
  });

  // Helper to compare dates by value, not reference
  const datesEqual = (date1, date2) => {
    if (!date1 && !date2) return true;
    if (!date1 || !date2) return false;
    return date1.getTime() === date2.getTime();
  };

  // Update selection when props change, but only when calendar is closed to prevent resetting position
  useEffect(() => {
    if (!isOpen && !isUserInteractingRef.current) {
      const newStart = startDate ? (startDate instanceof moment ? startDate.toDate() : new Date(startDate)) : null;
      const newEnd = endDate ? (endDate instanceof moment ? endDate.toDate() : new Date(endDate)) : null;
      
      // Only update if dates actually changed (by value, not reference)
      setSelectionRange(prev => {
        const startChanged = !datesEqual(prev.startDate, newStart);
        const endChanged = !datesEqual(prev.endDate, newEnd);
        
        if (startChanged || endChanged) {
          return {
            startDate: newStart || prev.startDate,
            endDate: newEnd || newStart || prev.endDate,
            key: 'selection',
          };
        }
        return prev;
      });
    }
  }, [startDate, endDate, isOpen]);

  const handleSelect = useCallback((ranges) => {
    const { selection } = ranges;
    isUserInteractingRef.current = true;
    setSelectionRange(selection);
    
    // Call onChange with moment objects
    if (onChange) {
      const start = selection.startDate ? moment(selection.startDate) : null;
      const end = selection.endDate ? moment(selection.endDate) : null;
      
      // Check if this is the first click (startDate and endDate are the same)
      // Only pass startDate on first click to prevent filtering/refreshing
      if (start && end && start.isValid() && end.isValid()) {
        const isFirstClick = start.isSame(end, 'day');
        
        if (isFirstClick) {
          // First click - only set startDate, don't set endDate
          onChange({ startDate: start, endDate: null });
        } else {
          // Second click - both dates are selected
          onChange({ startDate: start, endDate: end });
          // Close the picker when both dates are selected
          setTimeout(() => {
            setIsOpen(false);
            isUserInteractingRef.current = false;
          }, 100);
        }
      } else if (start && start.isValid()) {
        // Only start date is set
        onChange({ startDate: start, endDate: null });
      }
    }
    
    // Reset interaction flag after a short delay to allow for navigation
    setTimeout(() => {
      isUserInteractingRef.current = false;
    }, 300);
  }, [onChange]);

  const formatDisplayDate = (date) => {
    if (!date) return '';
    const momentDate = date instanceof moment ? date : moment(date);
    return momentDate.format('YYYY-MM-DD');
  };

  const displayText = startDate && endDate 
    ? `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`
    : t('dateRange.selectDateRange');

  // Handle calendar open/close
  const handleToggle = useCallback(() => {
    if (!isOpen) {
      // Opening calendar - mark as user interaction to prevent prop updates from resetting
      isUserInteractingRef.current = true;
      setIsOpen(true);
    } else {
      setIsOpen(false);
      // Reset interaction flag after closing with a delay to ensure all updates are complete
      setTimeout(() => {
        isUserInteractingRef.current = false;
      }, 200);
    }
  }, [isOpen]);

  // Keep interaction flag true while calendar is open to prevent any prop-based resets
  useEffect(() => {
    if (isOpen) {
      isUserInteractingRef.current = true;
    }
  }, [isOpen]);

  const handleClickAway = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      isUserInteractingRef.current = false;
    }, 100);
  }, []);

  // Memoize ranges array to prevent unnecessary re-renders
  const rangesArray = useMemo(() => {
    return [selectionRange];
  }, [
    selectionRange.startDate ? selectionRange.startDate.getTime() : null,
    selectionRange.endDate ? selectionRange.endDate.getTime() : null,
    selectionRange.key
  ]);


  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto', marginTop: margin === 'normal' ? 2 : 0, marginBottom: margin === 'normal' ? 1 : 0 }}>
      <Box
        ref={anchorRef}
        onClick={handleToggle}
        sx={{
          width: '100%',
          minHeight: 48,
          padding: '13px 16px',
          border: '1px solid var(--s10)',
          borderRadius: '14px',
          background: 'var(--field)',
          color: 'var(--tx)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          fontFamily: 'inherit',
          fontSize: 13.5,
          '&:hover': {
            borderColor: 'var(--s14)',
          },
          '&:focus-within': {
            borderColor: 'var(--primaryColor)',
          },
        }}
      >
        <Typography
          sx={{
            color: displayText === t('dateRange.selectDateRange') ? 'var(--mut)' : 'var(--tx)',
            fontSize: 13.5,
            fontFamily: 'inherit',
            fontWeight: 'inherit',
          }}
        >
          {displayText}
        </Typography>
      </Box>
      <Popper
        open={isOpen}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
        modifiers={[
          {
            name: 'preventOverflow',
            enabled: true,
          },
          {
            name: 'flip',
            enabled: true,
          },
        ]}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Box
            sx={{
              mt: 1,
              background: 'var(--card-solid)',
              border: '1px solid var(--s10)',
              boxShadow: 'var(--shadow)',
              borderRadius: '16px',
              overflow: 'hidden',
              position: 'relative',
              zIndex: 1300,
              '& *': {
                pointerEvents: 'auto',
              },
            }}
          >
            <DateRange
              ranges={rangesArray}
              onChange={handleSelect}
              months={2}
              direction="horizontal"
              showDateDisplay={false}
              showMonthAndYearPickers={true}
              moveRangeOnFirstSelection={false}
              retainEndDateOnFirstSelection={false}
              focusedRange={focusedRange}
              onRangeFocusChange={(focusedRange) => {
                isUserInteractingRef.current = true;
                setFocusedRange(focusedRange || [0, 0]);
                setTimeout(() => {
                  isUserInteractingRef.current = false;
                }, 100);
              }}
              minDate={new Date(1900, 0, 1)}
              maxDate={new Date(2100, 11, 31)}
            />
          </Box>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default DateRangePicker;
